import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { clearToken } from '../../auth/token';

// ─── QcHome ─────────────────────────────────────────────────────────────────
// Landing page shown right after a QC login. Plain by design -- just
// links into each QC sub-module. Add a new entry to MODULES below
// whenever a new QC screen is built; nothing else on this page needs to
// change, UNLESS the new module also needs a household phone number the
// way Space Planning QC does (see requiresPhonenumber below).
const MODULES = [
  {
    label: 'Management Dashboard',
    path: '/qc/management',
    description: 'How many customer accounts there are, day by day, over the last 2 months.',
  },
  {
    label: 'Material Sourcing QC',
    path: '/qc/materials',
    description: 'Review the candidate materials shown for each photo region before they reach users.',
  },
  {
    label: 'Space Planning QC',
    path: '/qc/space-planning',
    description: 'Review room layout options and furniture placement before they reach users.',
    // Reviewed one household at a time. The admin shares the relevant
    // phone number with the reviewer separately -- typing it in here
    // takes the reviewer straight to that household's own review queue
    // (see App.js's /qc/space-planning/:phonenumber route).
    requiresPhonenumber: true,
  },
];

export default function QcHome() {
  const navigate = useNavigate();
  const [phonenumber, setPhonenumber] = useState('');

  const handleLogout = () => {
    clearToken('qc');
    navigate('/qc/login');
  };

  const handleOpenSpacePlanning = (path) => {
    const trimmed = phonenumber.trim();
    if (!trimmed) return;
    navigate(`${path}/${encodeURIComponent(trimmed)}`);
  };

  return (
    <Box sx={{ maxWidth: 640, margin: '0 auto', padding: '40px 20px', fontFamily: 'sans-serif' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 600 }}>Quality Control</Typography>
        <Box
          component="button"
          type="button"
          onClick={handleLogout}
          sx={{
            fontSize: '12px', color: '#666', background: 'none', border: '1px solid #ccc',
            borderRadius: '3px', padding: '6px 12px', cursor: 'pointer',
          }}
        >
          Log out
        </Box>
      </Box>

      <Typography sx={{ fontSize: '14px', color: '#555', mb: 3 }}>
        Welcome. Pick a module below to start.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {MODULES.map((mod) =>
          mod.requiresPhonenumber ? (
            // Not a single clickable box like the others -- it needs a text
            // field inside it, so the box itself stays static and only the
            // "Review" button (and pressing Enter) navigates.
            <Box
              key={mod.path}
              sx={{
                textAlign: 'left', padding: '16px', border: '1px solid #ccc', borderRadius: '4px',
                background: '#fff',
              }}
            >
              <Typography component="h2" sx={{ fontSize: '15px', fontWeight: 600 }}>{mod.label}</Typography>
              <Typography sx={{ fontSize: '13px', color: '#666', mt: '4px', mb: '12px' }}>
                {mod.description}
              </Typography>

              <Box sx={{ display: 'flex', gap: '8px' }}>
                <Box
                  component="input"
                  value={phonenumber}
                  onChange={(e) => setPhonenumber(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleOpenSpacePlanning(mod.path); }}
                  placeholder="Household phone number"
                  aria-label="Household phone number"
                  inputMode="tel"
                  sx={{
                    flex: 1, fontSize: '13px', padding: '8px 10px', border: '1px solid #ccc',
                    borderRadius: '3px', fontFamily: 'inherit',
                  }}
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleOpenSpacePlanning(mod.path)}
                  disabled={!phonenumber.trim()}
                  sx={{
                    fontSize: '13px', padding: '8px 14px', borderRadius: '3px', border: 'none',
                    cursor: phonenumber.trim() ? 'pointer' : 'default',
                    background: phonenumber.trim() ? '#111' : '#ccc', color: '#fff',
                  }}
                >
                  Review
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              key={mod.path}
              component="button"
              type="button"
              onClick={() => navigate(mod.path)}
              sx={{
                textAlign: 'left', padding: '16px', border: '1px solid #ccc', borderRadius: '4px',
                background: '#fff', cursor: 'pointer', fontFamily: 'inherit',
                '&:hover': { borderColor: '#333' },
                '&:focus-visible': { outline: '2px solid #333', outlineOffset: '2px' },
              }}
            >
              <Typography component="h2" sx={{ fontSize: '15px', fontWeight: 600 }}>{mod.label}</Typography>
              <Typography sx={{ fontSize: '13px', color: '#666', mt: '4px' }}>{mod.description}</Typography>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
}
